










































































import { defineComponent, useContext, ref, useFetch, useRoute, computed } from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import NewsCardComponent from '~/components/pages/Other/NewsCard.vue';
import ShopBarInfo from '~/components/pages/Shop/ShopBarInfo.vue';
import ShopBlog from '~/components/pages/Shop/ShopBlog.vue';
import ShopCard from '~/components/pages/Shop/ShopCard.vue';
import ShopDetail from '~/components/pages/Shop/ShopDetail.vue';
import ShopEventCalendar from '~/components/pages/Shop/ShopEventCalendar.vue';
import AppLoader from '~/components/partials/App/AppLoader.vue';
import AppSeeMoreButton from '~/components/partials/App/AppSeeMoreButton.vue';
import GlobalHeaderMenu from '~/components/partials/Global/GlobalHeaderMenu.vue';
import LayoutDefault from '~/components/partials/Layout/LayoutDefault.vue';
import { useFilterBlogList } from '~/composables/blog/useFilterBlogList';
import { useTags } from '~/composables/contents/useTags';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useRuntimeConfig } from '~/composables/useRuntimeConfig';
import { useStore } from '~/composables/useStore';
import { DISPLAYED_SHOP_NEWS_NUM, BLOG_DISPLAY_COUNT, SHOP_ANCHOR } from '~/constants';
import { url, extractText } from '~/utils';
import { PageError } from '~/utils/error';
import { getSortedShopDistance } from '~/utils/shop';
import { ECSchema } from '~/types/api';
import { BlogArticle } from '~/types/modules/blog';
import { EventCardTags } from '~/types/modules/event';

export default defineComponent({
  components: {
    LayoutDefault,
    ShopDetail,
    ShopEventCalendar,
    ShopBarInfo,
    ShopBlog,
    AppSeeMoreButton,
    NewsCardComponent,
    ShopCard,
    AppLoader,
    GlobalHeaderMenu,
  },
  setup() {
    const { i18n } = useContext();
    const store = useStore();
    const route = useRoute();
    const { ENABLED_BLOG } = useRuntimeConfig();
    const shopDetail = ref<ECSchema<'saleslocation.ShowResponse'>>({
      address_name1: '',
      address_name2: '',
      area_display_order: 0,
      area_id: 0,
      area_name: '',
      business_hours: '',
      city_name: '',
      delete_flag: false,
      fax_no: '',
      google_map_latitude: '',
      google_map_longitude: '',
      googlemap_url: '',
      has_rental_cellar: false,
      id: 0,
      is_closed: false,
      mail_address: '',
      main_image_url: '',
      menu_business_hours: '',
      menu_explanation: '',
      menu_image01_url: '',
      menu_image02_url: '',
      menu_pdf: '',
      menu_seats: '',
      menu_special_pdf: '',
      menu_title: '',
      menu_unplugging_fee: '',
      my_shop: false,
      nearest_station: '',
      parent_area_display_order: 0,
      permalink: '',
      place_code: '',
      place_kind: 0,
      place_local_name: '',
      place_long_name: '',
      place_name: '',
      post_no: '',
      sales_location_display_order: 0,
      shop_drink_wine: false,
      shop_tag_line: '',
      state_name: '',
      store_introduction: '',
      store_types: [],
      tel_no: '',
    });
    const nearbySalesLocationList = ref<ECSchema<'saleslocation.SalesLocation'>[]>([]);
    const shopPermalink = route.value.params.permalink;
    // お知らせ記事表示数を制御するカウント
    const displayNewsCounts = ref(1);
    // お知らせのオフセットを制御するカウント
    const offsetCounts = ref(1);
    const shouldSeeMoreButton = ref(true);
    // イベントの初期化フラグ
    const initializedEventFlg = ref(false);
    // 全体の初期化が完了したかのフラグ
    const initializedFlg = ref(false);
    const hasNoArticles = ref(false);
    const allEventTags = ref<EventCardTags[]>([]);
    const { allTagNames } = useTags();
    const { fetchBlogArticles } = useFilterBlogList();
    const articles = ref<BlogArticle[]>([]);

    // クエリがあればクエリを除いた canonical を返す
    const canonical = computed(() => {
      const { path, query } = route.value;

      if (Object.keys(query).length > 0) {
        return path;
      }
    });

    const { fetchState } = useFetch(async () => {
      try {
        // 店舗IDを最初に取得する
        await fetchSalesLocationDetail();
        await Promise.all(
          [
            async () => {
              try {
                await fetchNewsOfShop();
              } catch {
                // 何もしない
              }
            },
            async () => {
              try {
                await fetchAllSalesLocation();
              } catch {
                // 近隣のショップ情報を空にする
                nearbySalesLocationList.value = [];
              }
            },
            async () => {
              try {
                await fetchEvents(dayjs().format('YYYY-MM-DD'), 1);
              } catch {
                // イベント一覧情報を初期化し、初期化フラグもtrueに設定
                store.commit('event/list/resetShopEventList');
                initializedEventFlg.value = true;
              }
            },
            async () => {
              try {
                await fetchDatesOfEvent();
              } catch {
                // イベント一覧情報を初期化し、初期化フラグもtrueに設定
                store.commit('event/list/resetShopEventList');
                initializedEventFlg.value = true;
              }
            },
            async () => {
              try {
                await fetchArticles();
              } catch {
                // 何もしない
              }
            },
          ].map((fn) => fn())
        );
      } catch {
        throw new PageError(404, {});
      } finally {
        initializedFlg.value = true;
      }
    });

    /** 拠点詳細取得 */
    const fetchSalesLocationDetail = async () => {
      shopDetail.value = await store.dispatch('modules/salesLocation/fetchSalesLocationByID', {
        // 必須クエリなので0を入れておく（API側からの要望）
        path: { id: 0 },
        query: { is_permalink: true, target_permalink: shopPermalink },
      });
    };

    /** 拠点一覧取得(近隣のショップ用) */
    const fetchAllSalesLocation = async () => {
      const salesLocationResponse = await store.dispatch('modules/salesLocation/fetchAllSalesLocations', {
        query: {
          limit: 2000,
          page: 1,
        },
      });
      // 拠点一覧か、距離を比較する元のショップIDがなければ早期リターン
      if (!salesLocationResponse.data || !shopDetail.value.id) {
        return;
      }
      // 近い順でソートされた店舗一覧のうち、2番目から3つ取得(1番目は同一店舗のため)
      const sortedDistance = getSortedShopDistance(salesLocationResponse.data, shopDetail.value.id).slice(1, 4);
      // 拠点一覧から同じ店舗IDのものを取得する
      nearbySalesLocationList.value = salesLocationResponse.data.filter((shop) => {
        return sortedDistance.some(({ id }) => shop.id === id);
      });
    };

    /** ショップニュースの初期4件読み込み */
    const fetchNewsOfShop = async () => {
      if (shopDetail.value.id) {
        store.commit('modules/news/resetShopNewsList');
        await store.dispatch('modules/news/fetchNewsOfShop', {
          limit: DISPLAYED_SHOP_NEWS_NUM + 1,
          shopIds: [shopDetail.value.id],
        });
        displaySeeMoreButton();
      }
    };

    /** ショップニュースの追加読み込み */
    const fetchAdditionalNewsOfShop = async () => {
      if (shopDetail.value.id) {
        await store.dispatch('modules/news/fetchNewsOfShop', {
          limit: DISPLAYED_SHOP_NEWS_NUM,
          offset: DISPLAYED_SHOP_NEWS_NUM * offsetCounts.value + 1,
          shopIds: [shopDetail.value.id],
        });
        offsetCounts.value++;
        displaySeeMoreButton();
      }
    };

    const shopNewsList = computed(() => {
      return store.getters['modules/news/getLimitedShopNewsList'](displayNewsCounts.value);
    });

    /** イベント一覧情報取得 */
    const fetchEvents = async (selectedDay: string, eventPageNo: number) => {
      // 初期化の場合のみ初期化フラグをfalseにし、storeを空にする
      if (eventPageNo === 1) {
        initializedEventFlg.value = false;
        store.commit('event/list/resetShopEventList');
        store.commit('event/list/resetTotalEventCounts');
      }
      await store.dispatch('event/list/fetchEventList', {
        query: {
          dateFrom: selectedDay,
          shop: [shopDetail.value.place_code.toString()],
          page: eventPageNo,
          limit: 3,
          sort: 'date',
        },
      });
      initializedEventFlg.value = true;

      allEventTags.value = allEventTags.value.concat((await allTagNames(store.state.event.list.shopEventList)) || []);
    };

    /** イベント開催日を前後半年分取得 */
    const fetchDatesOfEvent = async () => {
      await store.dispatch('event/list/fetchDatesOfEvent', {
        query: {
          dateFrom: dayjs().subtract(6, 'month').format('YYYY-MM-DD'),
          shop: [shopDetail.value.place_code.toString()],
        },
      });
    };

    const fetchArticles = async () => {
      const { items } = await fetchBlogArticles({
        id: shopDetail.value.id.toString(),
        listDisplayFlag: 1,
        detailDisplayFlag: 1,
        isCurrentlyPublished: true,
        // 最大三件を表示するので、固定で 3
        count: 3,
      });
      articles.value = items;
      if (articles.value.length === 0) hasNoArticles.value = true;
      // レイアウト表示用、三つ以下の場合だったら空データを追加する
      while (articles.value.length < 3) articles.value.push({} as any);
    };

    /** barが併設されているか */
    const hasBar = computed(() => {
      // そもそもnullであれば false
      if (!shopDetail.value.store_types) {
        return false;
      } else {
        return (
          shopDetail.value.store_types.filter((type) => {
            return type === 'bar' || type === 'bar_stool' || type === 'bar_table';
          }).length > 0
        );
      }
    });

    /** ショップニュースをもっと見る押下時の処理 */
    const seeMoreShopNews = async () => {
      displayNewsCounts.value++;
      await fetchAdditionalNewsOfShop();
    };

    // ストアの記事数によって「もっと見る」ボタンの表示を制御する
    const displaySeeMoreButton = () => {
      const shopNewsCounts = store.state.modules.news.newsListOfShop.length;
      // 上限+1の記事数が存在していれば「もっと見る」ボタンを表示する（5件存在していれば初期描画時に表示する）
      shouldSeeMoreButton.value = shopNewsCounts === DISPLAYED_SHOP_NEWS_NUM * offsetCounts.value + 1;
    };

    useLayoutProps(() => ({
      fetchState,
      layout: {
        header: 'always',
        headerMenu: 'never',
        footerMenu: 'always',
      },
      head: {
        title: shopDetail.value?.place_short_name || shopDetail.value?.place_long_name || '',
        description: shopDetail.value.store_introduction,
        canonical: canonical.value,
      },
      breadcrumbs: [
        { title: i18n.t('Other-02.pageTitle').toString(), to: { path: url('SHOP_LIST') } },
        {
          title: shopDetail.value?.place_short_name || shopDetail.value?.place_long_name || '',
          to: { path: url('SHOP_DETAIL', { permalink: shopPermalink }) },
        },
      ],
    }));

    return {
      ENABLED_BLOG,
      BLOG_DISPLAY_COUNT,
      SHOP_ANCHOR,
      url,
      extractText,
      dayjs,
      shopDetail,
      shopNewsList,
      nearbySalesLocationList,
      shouldSeeMoreButton,
      hasBar,
      initializedFlg,
      initializedEventFlg,
      seeMoreShopNews,
      fetchEvents,
      fetchDatesOfEvent,
      allEventTags,
      hasNoArticles,
      articles,
    };
  },
});
